import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { LOGO_DARK } from "@/lib/brand";
import { useReveal } from "@/hooks/use-reveal";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import PublicNav from "@/components/public/PublicNav";
import { BlogIndexSkeleton } from "@/components/public/Skeletons";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export default function BlogIndex() {
  const [posts, setPosts] = useState(null);
  const [settings, setSettings] = useState(null);
  const revealRef = useReveal(!!posts);

  useEffect(() => {
    (async () => {
      const [b, s] = await Promise.all([
        api.get("/content/blog"),
        api.get("/content/settings"),
      ]);
      setPosts(b.data.filter((p) => p.enabled !== false));
      setSettings(s.data);
    })().catch(console.error);
  }, []);

  useEffect(() => {
    if (settings?.seo_title) {
      document.title = `Blog · ${settings.seo_title}`;
    } else {
      document.title = "Blog · Aiyavi Retreat";
    }
  }, [settings]);

  if (!posts) {
    return <BlogIndexSkeleton />;
  }

  const [featured, ...rest] = posts;

  return (
    <div ref={revealRef} data-testid="blog-index" className="bg-earth-50 min-h-screen">
      <PublicNav settings={settings} alwaysSolid sectionPrefix="/" />

      {/* Hero */}
      <section className="px-6 pt-28 sm:pt-32 pb-10 bg-gradient-to-b from-earth-900 to-earth-50 text-white">
        <div className="max-w-5xl mx-auto reveal">
          <span className="text-xs sm:text-sm font-medium uppercase tracking-[0.22em] text-[#FF6B35]">Journal</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light mt-3 leading-[1.05] tracking-tight">
            Notes from <span className="italic text-white/70">the farmhouse.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-white/75 font-light text-base sm:text-lg leading-relaxed">
            Slow weekends, hosting guides and quiet observations from life at Aiyavi.
          </p>
        </div>
      </section>

      {/* List */}
      <section className="px-6 pb-20 pt-2">
        <div className="max-w-7xl mx-auto">
          {posts.length === 0 && (
            <div className="py-20 text-center text-earth-500">No posts yet — check back soon.</div>
          )}

          {featured && (
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`blog-featured-${featured.slug}`}
              className="reveal group block rounded-3xl overflow-hidden bg-white border border-earth-100 hover:shadow-[0_30px_60px_-30px_rgba(60,40,20,0.35)] transition-shadow duration-500"
            >
              <div className="grid lg:grid-cols-12 gap-0">
                <div className="lg:col-span-7 relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                  {featured.cover_url ? (
                    <img
                      src={featured.cover_url}
                      alt={featured.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-earth-300 to-earth-500" />
                  )}
                </div>
                <div className="lg:col-span-5 p-8 sm:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[#FF6B35]">
                    <span>Featured</span>
                    <span className="w-6 h-px bg-[#FF6B35]" />
                    <span className="text-earth-500 flex items-center gap-1.5">
                      <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {formatDate(featured.date)}
                    </span>
                  </div>
                  <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-earth-900 mt-4 leading-[1.1] group-hover:text-[#FF6B35] transition-colors">
                    {featured.title}
                  </h2>
                  {featured.excerpt && (
                    <p className="mt-4 text-earth-600 font-light text-base sm:text-lg leading-relaxed">
                      {featured.excerpt}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-2 mt-6 text-sm text-earth-900 font-medium">
                    Read story <ArrowUpRight className="w-4 h-4 text-[#FF6B35] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
                  </span>
                </div>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8 stagger">
              {rest.map((p, i) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="reveal group bg-white rounded-3xl overflow-hidden border border-earth-100 hover:shadow-[0_24px_50px_-25px_rgba(60,40,20,0.3)] transition-shadow duration-500 flex flex-col"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    {p.cover_url ? (
                      <img
                        src={p.cover_url}
                        alt={p.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-earth-200 to-earth-400" />
                    )}
                    <div className="absolute top-4 right-4 font-serif italic text-xs text-white/90 bg-earth-900/50 backdrop-blur px-3 py-1 rounded-full">
                      /{String(i + 2).padStart(2, "0")}
                    </div>
                  </div>
                  <div className="p-7 flex flex-col gap-3 flex-1">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-earth-500">
                      <CalendarDays className="w-3.5 h-3.5" strokeWidth={1.5} />
                      {formatDate(p.date)}
                    </div>
                    <h3 className="font-serif text-2xl text-earth-900 leading-tight group-hover:text-[#FF6B35] transition-colors">
                      {p.title}
                    </h3>
                    {p.excerpt && (
                      <p className="text-earth-600 text-sm leading-relaxed line-clamp-3">{p.excerpt}</p>
                    )}
                    <span className="inline-flex items-center gap-2 mt-auto text-sm text-earth-900 font-medium pt-2">
                      Read <ArrowUpRight className="w-4 h-4 text-[#FF6B35] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" strokeWidth={1.5} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 pb-10 pt-6 bg-beige/40 border-t border-earth-100">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-4 text-sm text-earth-600">
          <img src={LOGO_DARK} alt="Aiyavi Retreat" className="h-10 w-auto object-contain" />
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-earth-900" data-testid="blog-footer-home">Home</Link>
            <a href="/admin/login" className="hover:text-earth-900">Admin</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
